import React, { useEffect, useMemo, useState } from 'react'
import { ButtonProps, Flex, ModalCloseButton } from '@chakra-ui/react'
import ModalWithSections from '~editor/ModalWithSections'
import {
  Button as ModalButton,
  Label,
  Title,
} from '~editor/ModalWithSections/components'
import { library } from '~/core/componentLibrary'
import { PuzzleIcon } from '~editor/components/icons'
import { useSelector } from 'react-redux'
import useDispatch from '~editor/hooks/useDispatch'
import {
  AclEnum,
  ComponentCollection,
  ComponentCollectionRecent,
  LibraryCollectionMap,
} from '~core/global.type'
import { RecentIcon } from '~editor/components/icons/ComponentLibrary'
import { getComponentsLibrary } from '~/editor/selectors/editor'
import Panel from './Panels/Interactables/Buttons'
import { ChevronLeftIcon, ChevronRightIcon } from '@chakra-ui/icons'
import {
  Pagination,
  PaginationNext,
  PaginationPage,
  PaginationPageGroup,
  PaginationPrevious,
  PaginationSeparator,
  usePagination,
} from '@getgrass/pagination'
import { addSpaceBeforeCapitalLetters } from '~utils/domHelpers'
import useRecentComponents from '~editor/hooks/useRecentComponents'
import { COMPONENT_LIBRARY_ITEMS_PER_PAGE } from '~editor/utils/constants'
import { useUser, useUserAbilities } from '~editor/hooks/useUser'
import {
  ComponentCollectionIcons,
  ForgeComponent,
  IForgeComponents,
} from '~core/forge.type'
import { useVersions } from '~editor/hooks/useVersions'
import { MissingComponent } from '~core/components/missingComponent'

export interface PanelProps {
  onClose: () => void
  components: [string, ForgeComponent][]
}

const ComponentLibrary = () => {
  const [panelComponents, setPanelComponents] = useState<IForgeComponents>({})
  const [paginationPageCount, setPaginationPageCount] = useState(1)

  const { currentPage, setCurrentPage, pages } = usePagination({
    pagesCount: paginationPageCount,
    initialState: { currentPage: 1 },
    limits: {
      outer: 1,
      inner: 2,
    },
  })

  const slicedComponents = useMemo(() => {
    return Object.entries(panelComponents || {}).slice(
      currentPage * COMPONENT_LIBRARY_ITEMS_PER_PAGE -
        COMPONENT_LIBRARY_ITEMS_PER_PAGE,
      currentPage * COMPONENT_LIBRARY_ITEMS_PER_PAGE,
    )
  }, [panelComponents, currentPage])

  const dispatch = useDispatch()
  const isOpen = useSelector(getComponentsLibrary)
  const onClose = () => {
    dispatch.editor.toggleComponentsLibrary(false)
  }

  const [activeCollection, setActiveCollection] = useState<
    typeof ComponentCollectionRecent | ComponentCollection
  >(ComponentCollectionRecent)

  const { recentComponents } = useRecentComponents()

  useEffect(() => {
    setCurrentPage(1)
  }, [activeCollection])

  const user = useUser()
  const versions = useVersions()
  const abilities = useUserAbilities()
  const components = useMemo(() => {
    if (!user || !abilities || !versions) {
      return []
    }
    return library.getAll(user, abilities, { version: versions.hub }, 'preview')
  }, [user, abilities, versions])

  useEffect(() => {
    if (activeCollection !== ComponentCollectionRecent) {
      const items = library.getAll(
        user,
        abilities,
        {
          collection: activeCollection,
          version: versions?.hub,
        },
        'preview',
      )
      setPanelComponents(items)
      setPaginationPageCount(
        Math.ceil(
          Object.keys(items || {}).length / COMPONENT_LIBRARY_ITEMS_PER_PAGE,
        ),
      )
    } else {
      try {
        Promise.all(
          recentComponents
            .map((recentComponent) => library.get(recentComponent))
            .filter((component) => component !== MissingComponent),
        ).then((componentsArray) => {
          const components = componentsArray.reduce((acc, component) => {
            acc[component.forge.id] = component

            return acc
          }, {} as IForgeComponents)

          setPanelComponents(components)
          setPaginationPageCount(
            Math.ceil(
              Object.keys(components || {}).length /
                COMPONENT_LIBRARY_ITEMS_PER_PAGE,
            ),
          )
        })
      } catch {
        setPanelComponents({})
        setPaginationPageCount(
          Math.ceil(Object.keys({}).length / COMPONENT_LIBRARY_ITEMS_PER_PAGE),
        )
      }
    }
  }, [activeCollection, recentComponents, user, abilities, versions])

  const headerTitle = useMemo(() => {
    return (
      <Title leftIcon={<PuzzleIcon color="gameglass.50" />}>
        {'Components'}
      </Title>
    )
  }, [])

  const headerBody = useMemo(() => {
    return (
      <Flex justifyContent="flex-end" alignItems="center">
        <ModalCloseButton color="#9BA3B0" />
      </Flex>
    )
  }, [])

  const sideBody = useMemo(() => {
    if (!components || !user) {
      return null
    }
    const libraryCollections = Object.entries(LibraryCollectionMap).reduce(
      (acc, [libraryCollection, componentCollections]) => {
        acc[addSpaceBeforeCapitalLetters(libraryCollection)] =
          componentCollections
            .filter((componentCollection) =>
              Object.entries(components).some(
                ([_, component]) =>
                  component.forge.collection === componentCollection,
              ),
            )
            .map((componentCollection) => {
              const Icon = ComponentCollectionIcons[componentCollection]
              return (
                <ModalButton
                  isActive={activeCollection === componentCollection}
                  key={componentCollection}
                  onClick={() => {
                    setActiveCollection(componentCollection)
                  }}
                  leftIcon={Icon ? <Icon /> : undefined}
                >
                  {addSpaceBeforeCapitalLetters(componentCollection)}
                </ModalButton>
              )
            })

        return acc
      },
      {} as Record<string, JSX.Element[]>,
    )

    return (
      <>
        <ModalButton
          isActive={activeCollection === ComponentCollectionRecent}
          key={ComponentCollectionRecent}
          onClick={() => {
            setActiveCollection(ComponentCollectionRecent)
          }}
          leftIcon={<RecentIcon />}
        >
          {ComponentCollectionRecent}
        </ModalButton>
        {Object.entries(libraryCollections).map(
          ([libraryCollection, elements]) => {
            if (!elements.length) {
              return null
            }
            return (
              <React.Fragment key={libraryCollection}>
                <Label textTransform={'uppercase'}>{libraryCollection}</Label>
                {elements}
              </React.Fragment>
            )
          },
        )}
      </>
    )
  }, [components, activeCollection, user])

  const mainBody = useMemo(() => {
    return <Panel onClose={onClose} components={slicedComponents} />
  }, [activeCollection, slicedComponents])

  const footer = useMemo(() => {
    const _hover: ButtonProps = {
      _hover: {
        bg: 'rgba(73, 230, 155, 30%)',
        color: '#49E69B',
        border: '1px solid #49E69B',
      },
    }

    const baseStyles: ButtonProps = {
      w: '39px',
      h: '37px',
      fontSize: '14px',
      color: '#878FA6',
    }

    const normalStyles: ButtonProps = {
      ...baseStyles,
      _hover,
      bg: '#232836',
      border: '1px solid #3E455A',
    }

    const activeStyles: ButtonProps = {
      ...baseStyles,
      _hover,
      bg: 'rgba(73, 230, 155, 10%)',
      border: '1px solid #49E69B',
      color: '#49E69B',
    }

    const separatorStyles: ButtonProps = {
      w: '39px',
      h: '37px',
      _hover,
      bg: '#232836',
      border: '1px solid #3E455A',
    }

    return (
      <Flex flexDirection="row" w="100%" justifyContent="flex-start">
        <Pagination
          {...activeStyles}
          currentPage={currentPage}
          onPageChange={setCurrentPage}
          pagesCount={paginationPageCount}
        >
          <>
            {paginationPageCount > 1 && (
              <PaginationPrevious mr="15px" {...normalStyles}>
                <ChevronLeftIcon />
              </PaginationPrevious>
            )}

            <PaginationPageGroup
              direction={'row'}
              alignItems="flex-start"
              separator={<PaginationSeparator {...separatorStyles} />}
            >
              {pages.map((page: number) => (
                <PaginationPage
                  {...normalStyles}
                  _current={activeStyles}
                  key={`pagination_page_${page}`}
                  page={page}
                />
              ))}
            </PaginationPageGroup>

            {paginationPageCount > 1 && (
              <PaginationNext ml="15px" {...normalStyles}>
                <ChevronRightIcon />
              </PaginationNext>
            )}
          </>
        </Pagination>
      </Flex>
    )
  }, [currentPage, setCurrentPage, panelComponents, paginationPageCount])

  return (
    (isOpen || null) && (
      <ModalWithSections
        isOpen={isOpen}
        onClose={onClose}
        headerTitle={headerTitle}
        headerBody={headerBody}
        sideBody={sideBody}
        mainBody={mainBody}
        footer={paginationPageCount > 1 ? footer : undefined}
        contentHeight="900px"
        footerHeight="67px"
        contentOverflow="auto"
      />
    )
  )
}

export default ComponentLibrary
